import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Copy,
  Eye,
  FileAudio,
  FileText,
  FileVideo,
  Image as ImageIcon,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import ConfirmDialog from '../../components/MasterAdmin/ui/ConfirmDialog';
import type { MediaAsset, MediaFileType } from '../../models/MasterAdmin/mediaAsset';
import {
  buildMediaSampleResponse,
  fileTypeLabel,
  isValidMediaKey,
  normalizeMediaKeyInput,
} from '../../models/MasterAdmin/mediaAsset';
import {
  deleteMediaAsset,
  fetchMediaAssets,
  fetchMediaMeta,
  fetchMediaPreviewByKey,
  toggleMediaAssetActive,
  uploadMediaAsset,
} from '../../services/MasterAdmin/mediaAssetService';

function FileTypeIcon({ type, className = 'h-5 w-5' }: { type: MediaFileType; className?: string }) {
  if (type === 'image') return <ImageIcon className={className} />;
  if (type === 'audio') return <FileAudio className={className} />;
  if (type === 'video') return <FileVideo className={className} />;
  if (type === 'document') return <FileText className={className} />;
  return <FileText className={className} />;
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

async function copyText(text: string) {
  await navigator.clipboard.writeText(text);
}

const IntegrationInfoModal: React.FC<{ asset: MediaAsset | null; onClose: () => void }> = ({
  asset,
  onClose,
}) => {
  if (!asset) return null;

  const samplePayload = asset.sampleResponse ?? buildMediaSampleResponse(asset);
  const sampleJson = JSON.stringify(samplePayload, null, 2);
  const fetchApiUrl = asset.fetchApiUrl || `/api/media/key/${encodeURIComponent(asset.key)}`;
  const listApiUrl = asset.listApiUrl || '/api/media';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Integration info</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {asset.name} · <code className="text-xs">{asset.key}</code> · v{asset.version}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 text-sm">
          <div>
            <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">Fetch this asset (recommended)</p>
            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
              <code className="flex-1 break-all text-xs text-gray-800 dark:text-gray-200">
                GET {fetchApiUrl}
              </code>
              <button
                type="button"
                onClick={() => copyText(fetchApiUrl).then(() => toast.success('API URL copied'))}
                className="shrink-0 rounded p-1.5 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div>
            <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">List all active media</p>
            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
              <code className="flex-1 break-all text-xs text-gray-800 dark:text-gray-200">
                GET {listApiUrl}
              </code>
              <button
                type="button"
                onClick={() => copyText(listApiUrl).then(() => toast.success('List API URL copied'))}
                className="shrink-0 rounded p-1.5 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div>
            <p className="mb-1 font-medium text-gray-700 dark:text-gray-300">Sample response</p>
            <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-950 p-4 text-xs text-green-300 dark:border-gray-700">
              {sampleJson}
            </pre>
            <button
              type="button"
              onClick={() => copyText(sampleJson).then(() => toast.success('Sample JSON copied'))}
              className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
            >
              <Copy className="h-3.5 w-3.5" />
              Copy sample JSON
            </button>
          </div>

          <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200">
            Use <strong>key</strong> as the stable identifier in your app. The <strong>fileUrl</strong> in the API
            response points to the stored file — do not hardcode storage URLs in Flutter, web, or admin code.
          </div>
        </div>
      </div>
    </div>
  );
};

const MediaPreviewModal: React.FC<{ asset: MediaAsset | null; onClose: () => void }> = ({
  asset,
  onClose,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!asset) {
      setPreviewUrl(null);
      return;
    }
    setLoading(true);
    fetchMediaPreviewByKey(asset.key)
      .then((data) => setPreviewUrl(data.fileUrl))
      .catch(() => toast.error('Could not load preview'))
      .finally(() => setLoading(false));
  }, [asset]);

  if (!asset) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{asset.name}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {fileTypeLabel(asset.fileType)} · v{asset.version}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
          {loading ? (
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          ) : previewUrl && asset.fileType === 'image' ? (
            <img src={previewUrl} alt={asset.name} className="max-h-80 max-w-full rounded-lg object-contain" />
          ) : previewUrl && asset.fileType === 'video' ? (
            <video src={previewUrl} controls className="max-h-80 max-w-full rounded-lg" />
          ) : previewUrl && asset.fileType === 'audio' ? (
            <audio src={previewUrl} controls className="w-full" />
          ) : previewUrl ? (
            <a href={previewUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline dark:text-blue-400">
              Open file in new tab
            </a>
          ) : (
            <span className="text-sm text-gray-500">Preview unavailable</span>
          )}
        </div>
      </div>
    </div>
  );
};

const MediaManagementPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState<MediaAsset[]>([]);
  const [fileTypes, setFileTypes] = useState<MediaFileType[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadKey, setUploadKey] = useState('');

  const [previewAsset, setPreviewAsset] = useState<MediaAsset | null>(null);
  const [infoAsset, setInfoAsset] = useState<MediaAsset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MediaAsset | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);

  const normalizedUploadKey = useMemo(() => normalizeMediaKeyInput(uploadKey), [uploadKey]);
  const uploadKeyValid = isValidMediaKey(normalizedUploadKey);

  const loadMeta = useCallback(async () => {
    const meta = await fetchMediaMeta();
    setFileTypes(meta.fileTypes);
  }, []);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchMediaAssets({
        search: search.trim() || undefined,
        fileType: filterType || undefined,
        isActive: filterActive === 'all' ? undefined : filterActive === 'active',
        page: pagination.page,
        limit: pagination.limit,
      });
      setItems(result.data);
      setPagination(result.pagination);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load media assets');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterActive, pagination.page, pagination.limit]);

  useEffect(() => {
    loadMeta().catch(() => toast.error('Failed to load media options'));
  }, [loadMeta]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const selectedFilePreview = useMemo(() => {
    if (!selectedFile?.type.startsWith('image/')) return null;
    return URL.createObjectURL(selectedFile);
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (selectedFilePreview) URL.revokeObjectURL(selectedFilePreview);
    };
  }, [selectedFilePreview]);

  const resetUploadForm = () => {
    setSelectedFile(null);
    setUploadName('');
    setUploadKey('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Choose a file to upload');
      return;
    }
    if (!uploadKeyValid) {
      toast.error('Enter a valid key (e.g. ambulance_booking_audio)');
      return;
    }
    try {
      setUploading(true);
      await uploadMediaAsset({
        file: selectedFile,
        key: normalizedUploadKey,
        name: uploadName.trim() || selectedFile.name,
      });
      toast.success('Media uploaded — use the API key to fetch it in your app');
      setUploadOpen(false);
      resetUploadForm();
      await loadItems();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleActive = async (asset: MediaAsset) => {
    try {
      setActionId(asset.id);
      await toggleMediaAssetActive(asset.id, !asset.isActive);
      toast.success(asset.isActive ? 'Media deactivated' : 'Media activated');
      await loadItems();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Update failed');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteBusy(true);
      await deleteMediaAsset(deleteTarget.id);
      toast.success('Media permanently deleted');
      setDeleteTarget(null);
      await loadItems();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Media management</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload files to storage and fetch them in your app or website via API using a stable key — no hardcoded storage URLs.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => loadItems()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              resetUploadForm();
              setUploadOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Upload file
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900 md:grid-cols-3">
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setPagination((p) => ({ ...p, page: 1 }));
            setSearch(e.target.value);
          }}
          placeholder="Search by name or key…"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
        <select
          value={filterType}
          onChange={(e) => {
            setPagination((p) => ({ ...p, page: 1 }));
            setFilterType(e.target.value);
          }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">All file types</option>
          {fileTypes.map((t) => (
            <option key={t} value={t}>
              {fileTypeLabel(t)}
            </option>
          ))}
        </select>
        <select
          value={filterActive}
          onChange={(e) => {
            setPagination((p) => ({ ...p, page: 1 }));
            setFilterActive(e.target.value as 'all' | 'active' | 'inactive');
          }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/80">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Key</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Version</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Updated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                    No media assets yet. Upload a file with a unique key to get started.
                  </td>
                </tr>
              ) : (
                items.map((asset) => (
                  <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                          <FileTypeIcon type={asset.fileType} className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="font-medium text-gray-900 dark:text-white">{asset.name}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                        {asset.key}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{fileTypeLabel(asset.fileType)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">v{asset.version}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          asset.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {asset.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(asset.updatedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          title="Integration info"
                          onClick={() => setInfoAsset(asset)}
                          className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Preview"
                          onClick={() => setPreviewAsset(asset)}
                          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title={asset.isActive ? 'Deactivate' : 'Activate'}
                          disabled={actionId === asset.id}
                          onClick={() => handleToggleActive(asset)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        >
                          {asset.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => setDeleteTarget(asset)}
                          className="rounded-lg p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-gray-700">
            <p className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50 dark:border-gray-600"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50 dark:border-gray-600"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {uploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload file</h2>
              <button
                type="button"
                onClick={() => {
                  setUploadOpen(false);
                  resetUploadForm();
                }}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">File</label>
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 px-4 py-8 hover:border-blue-400 dark:border-gray-600">
                  <Upload className="mb-2 h-8 w-8 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {selectedFile ? selectedFile.name : 'Choose image, audio, video, or document'}
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setSelectedFile(file);
                      if (file && !uploadName) setUploadName(file.name.replace(/\.[^.]+$/, ''));
                      if (file && !uploadKey) {
                        setUploadKey(
                          normalizeMediaKeyInput(file.name.replace(/\.[^.]+$/, ''))
                        );
                      }
                    }}
                  />
                </label>
                {selectedFilePreview && (
                  <img src={selectedFilePreview} alt="Preview" className="mt-3 max-h-40 rounded-lg object-contain" />
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Key <span className="text-red-500">*</span>
                </label>
                <input
                  value={uploadKey}
                  onChange={(e) => setUploadKey(e.target.value)}
                  placeholder="ambulance_booking_audio"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
                <p className={`mt-1 text-xs ${uploadKey && !uploadKeyValid ? 'text-red-500' : 'text-gray-500'}`}>
                  Unique identifier for API fetch · lowercase, numbers, underscores
                  {normalizedUploadKey && normalizedUploadKey !== uploadKey.trim().toLowerCase() && (
                    <> → will save as <code>{normalizedUploadKey}</code></>
                  )}
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Display name</label>
                <input
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                  placeholder="Ambulance Booking Instructions"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setUploadOpen(false);
                  resetUploadForm();
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={uploading || !selectedFile || !uploadKeyValid}
                onClick={handleUpload}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      <MediaPreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} />
      <IntegrationInfoModal asset={infoAsset} onClose={() => setInfoAsset(null)} />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Permanently delete media?"
        message="This removes the database record and deletes the file from storage. This cannot be undone."
        confirmLabel="Delete permanently"
        tone="danger"
        busy={deleteBusy}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default MediaManagementPage;
